import { TaskHandler } from '../TaskHandler';
import { OnInit, ElementRef, ViewChild, OnDestroy, ViewContainerRef, ComponentFactoryResolver, AfterContentInit, Directive } from '@angular/core';
import { IObjectDTO, DocumentDTO, TaskDTO } from '../../api/plyca-dtos';
import { FormConfig } from '../../components/forms/form.objects';
import { tap, finalize, switchMap, catchError } from 'rxjs/operators';
import { BaseFormInterface } from './baseForm.interface';
import { Configuration } from '../../conf/app.constants';
import { ServiceLocator } from '../../ServiceLocator';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import {
  Observable,
  of,
  throwError
} from 'rxjs';
import {
  ProjectEntityDTO,
  ValidationResultDTO,
  ResultTypeDTO,
  getResultTypeDTOLevel
} from '../../api/plyca-dtos-v2';
import * as lodash from 'lodash';
import { formPlycaComponent } from '../../components/forms/formPlyca.component';
import { TranslateService } from '@ngx-translate/core';
import { generateRandomIdentifier } from '../../ui.service';
import { FormService } from '../../components/forms/form.service';
import { validate } from 'json-schema';

/**
 * Definicion de los datos de la cabecera de la pantalla
 */
export class BaseFormHeaderInfo {
  titleKey: string;
  title: string;
  titleIcon: string;
  project: ProjectEntityDTO;
}

export class ValidationServerException extends Error {

  constructor(public validationResult: ValidationResultDTO, message?: string) {
    super(message || 'Error de validacion en el servidor');
    Object.setPrototypeOf(this, ValidationServerException.prototype);
  }
}

@Directive()
export abstract class BaseFormComponent<
  T extends IObjectDTO<any>,
  U extends Object
> extends TaskHandler implements BaseFormInterface<T>, OnInit, AfterContentInit, OnDestroy {
  /**
   * Modelo  con los datos de la pantalla
   */
  public model: U;
  public formConfig: FormConfig;
  protected configuration: Configuration;
  protected headerActionsService: HeaderActionsService;
  public translate: TranslateService;
  //protected formService: FormService;
  protected modalOpened: boolean;
  public windowHash: string;
  protected loaded = false;

  @ViewChild('rejectionPanelContainer', { read: ViewContainerRef, static: true })
  rejectionPanelContainer: ViewContainerRef;
  componentFactoryResolver: ComponentFactoryResolver;

  protected componentInitialized = false;
  protected formInitialized = false;

  constructor(protected elem: ElementRef) {
    super();
    this.configuration = ServiceLocator.injector.get(Configuration);
    this.headerActionsService = ServiceLocator.injector.get(
      HeaderActionsService

    );
    this.translate = ServiceLocator.injector.get(TranslateService);
    this.formService = ServiceLocator.injector.get(FormService);
    this.componentFactoryResolver = ServiceLocator.injector.get(ComponentFactoryResolver);
  }

  ngAfterContentInit(): void {
    if (this.formPlycaRef) {
      this.subscribe(this.formPlycaRef.getCompleted(), () => {
        this.formInitialized = true;
        this.cancelInitWait();
      });
    } else {
      this.formInitialized = true;
      this.cancelInitWait();
    }
  }

  protected cancelInitWait(): void {
    if (this.formInitialized && this.componentInitialized) {
      this.waitService.cancelWaiting();
    }
  }


  getDataObservable(): T {
    return {} as T;
  }

  ngOnInit(): void {
    console.log('Cargando componente ' + this.constructor.name);
    this.waitService.startWaiting();
    let objectId = this.getObjectIdentifier();
    let initDataObservable: Observable<any>;
    if (objectId) {
      // consultar dto
      initDataObservable = this.plycaService.get<T>(
        this.getServiceURL(),
        objectId,
        this.getObjectParams()
      );
    } else {
      initDataObservable = of(this.getDataObservable());
    }
    // inicializacion de modelo y formulario
    this.subscribeObserver(
      this.preLoadForm().pipe(
        switchMap(() => initDataObservable),
        switchMap((object: T) => this.dtoToModel(object)),
        tap((model: U) => {
          // cargar modelo de la pagina
          this.model = model;
        }),
        switchMap(() => this.createFormConfig()),
        switchMap((form: FormConfig) => this.configureFormConfig(form)),
        tap((form: FormConfig) => (this.formConfig = form)),
        switchMap(() => this.postLoadForm()),
        finalize(() => {
          // cargar datos cabecera
          this.initHeaderInfo();
          this.initHeaderActions();
          this.componentInitialized = true;
          // this.cancelInitWait();
        }),
        catchError((error: any) => {
          console.log('manageFormException 2', error);

          this.manageActionError(error);

          return throwError(error);
        })
      ), {
      error: (error: any) => this.waitService.cancelWaiting(),
      complete: () => this.loaded = true
    });
  }

  protected postLoadForm(): Observable<any> {
    return of({});
  }

  protected preLoadForm(): Observable<any> {
    return of({});
  }

  ngOnDestroy(): void {
    this.formPlycaRef = null;
    this.rejectionPanelContainer = null;
    this.headerActionsService.clearWindowActions(this.windowHash);
    super.ngOnDestroy();
  }

  public isLoaded() {
    return this.loaded;
  }

  protected createFormConfig(): Observable<FormConfig> {
    return of(new FormConfig(undefined, this.getFormSettingURL(), this.model));
  }

  protected initHeaderInfo() {
    if (!this.windowHash) {
      const headerInfo = this.getHeaderInfo();
      if (headerInfo) {
        if (headerInfo.project) {
          this.headerActionsService.setProject(
            this.elem,
            headerInfo.project,
            false
          );
        }
        if (headerInfo.title || headerInfo.titleKey || headerInfo.titleIcon) {
          this.headerActionsService.setTaskDescription(
            this.elem,
            headerInfo.titleKey
              ? this.translateService.instant(headerInfo.titleKey)
              : headerInfo.title,
            headerInfo.titleIcon,
            true,
            // tslint:disable-next-line:triple-equals
            this.windowHash != undefined
          );
        }
      }
      if (this.task) {
        this.headerActionsService.setShowDocumentsVisible(true);
        this.headerActionsService.setShowCalendarVisible(true);
      }
    }
  }

  /*
   * Metodos CRUD
   */


  protected save(showWait = true, model?: T): Observable<any> {
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));

    let insUpdtObs;

    if (modifiedDto.identifier) {
      insUpdtObs = this.update(showWait, modifiedDto, true)
    } else {
      insUpdtObs = this.insert(showWait, modifiedDto, true)
    }

    return insUpdtObs;
  }

  protected saveContext(showWait = true, model?: T, context?: string): Observable<any> {
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));

    let insUpdtObs;

    if (modifiedDto.identifier) {
      insUpdtObs = this.updateContext(showWait, model, context)
    } else {
      insUpdtObs = this.insertContext(showWait, model, context)
    }

    return insUpdtObs;
  }

  protected insert(showWait = true, model?: T, fromSave = false): Observable<any> {
    return this.insertContext(showWait, model, undefined, fromSave);
  }

  protected insertContext(showWait = true, model?: T, context?: string, fromSave = false): Observable<any> {
    if (showWait) {
      this.waitService.startWaiting();

    }
    if (this.formPlycaRef) {
      this.formPlycaRef.deleteFormErrors();
    }
    let modifiedDto = model;
    if (!fromSave) {
      modifiedDto = model
        ? model
        : this.modelToDTO(lodash.cloneDeep(this.model));
    }
    this.setTaskReference(modifiedDto);

    return this.plycaService.insert<T>(
      this.getServiceURL('insert'),
      modifiedDto,
      context
    ).pipe(
      catchError((error: any) => this.manageHttpError(error, false, true)),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );

  }

  protected update(showWait = true, model?: T, isModelCloned = false): Observable<any> {
    return this.updateContext(showWait, model, undefined, isModelCloned);
  }

  protected updateContext(showWait = true, model?: T, context?: string, isModelCloned = false): Observable<any> {
    if (showWait) {
      this.waitService.startWaiting();

    }
    if (this.formPlycaRef) {
      this.formPlycaRef.deleteFormErrors();
    }
    let modifiedDto = model;
    if (!isModelCloned) {
      modifiedDto = model
        ? model
        : this.modelToDTO(lodash.cloneDeep(this.model));
    }
    this.setTaskReference(modifiedDto);
    return this.plycaService.updateObject<T>(
      this.getServiceURL('update'),
      modifiedDto,
      context
    ).pipe(
      catchError((error: any) => this.manageHttpError(error, false, true)),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );
  }

  /**
   * metodo que inserta/actualiza si tras validar el DTO el nivel de error devuelto por la validacion
   * es estrictamente superior al indicado por parametro
   */
  protected saveIfValidationLevel(showWait = true, level: ResultTypeDTO, model?: T): Observable<any> {
    if (showWait) {
      this.waitService.startWaiting();
    }
    if (ResultTypeDTO.FATAL === level) {
      throw Error('El parametro level debe ser inferior a FATAL');
    }
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));
    return this.validate(false, modifiedDto).pipe(
      switchMap((validation: ValidationResultDTO) => {
        if (getResultTypeDTOLevel(validation.result) > getResultTypeDTOLevel(level)) {
          // TODO el devolver un objeto ValidationResultDTO implica que el que llama a este metodo
          // tiene que ver si la respuesta no es de tipo ValidationResultDTO para saber si se guardo o no el objeto
          // ¿No sería mejor lanzar excepcion ValidationServerException?
          return of(validation);
        } else {
          if (this.task) {
            return this.processTaskObservable();
          } else {
            return this.save(false, modifiedDto);
          }
        }
      }),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );
  }

  protected saveIfValid(showWait = true, model?: T): Observable<any> {
    return this.saveIfValidationLevel(showWait, ResultTypeDTO.ERROR, model);
  }

  protected saveIfValidationLevelContext(showWait = true, level: ResultTypeDTO, model?: T, context?: string): Observable<any> {
    if (showWait) {
      this.waitService.startWaiting();
    }
    if (ResultTypeDTO.FATAL === level) {
      throw Error('El parametro level debe ser inferior a FATAL');
    }
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));
    return this.validateContext(false, modifiedDto, context).pipe(
      switchMap((validation: ValidationResultDTO) => {
        if (getResultTypeDTOLevel(validation.result) > getResultTypeDTOLevel(level)) {
          // TODO el devolver un objeto ValidationResultDTO implica que el que llama a este metodo
          // tiene que ver si la respuesta no es de tipo ValidationResultDTO para saber si se guardo o no el objeto
          // ¿No sería mejor lanzar excepcion ValidationServerException?
          return of(validation);
        } else {
          if (this.task) {
            return this.processTaskObservable();
          } else {
            return this.saveContext(false, modifiedDto, context);
          }
        }
      }),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );
  }

  protected saveIfValidContext(showWait = true, model?: T, context?: string): Observable<any> {
    return this.saveIfValidationLevelContext(showWait, ResultTypeDTO.ERROR, model, context);
  }

  protected delete(showWait = true, model?: T): Observable<any> {
    if (showWait) {
      this.waitService.startWaiting();

    }
    if (this.formPlycaRef) {
      this.formPlycaRef.deleteFormErrors();
    }
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));
    this.setTaskReference(modifiedDto);
    let deleteObs = this.plycaService.delete<T>(
      this.getServiceURL(),
      modifiedDto.identifier
    ).pipe(
      catchError((error: any) => this.manageHttpError(error, false)),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );
    return deleteObs;
  }

  manageHttpError(error: any,
    cancelWait?: boolean,
    showValidationMessageInPopup?: boolean): Observable<any> {
    if (this.formService.isValidationResponse(error)) {
      let validation = error.error as ValidationResultDTO;
      this.manageActionError(validation, cancelWait, showValidationMessageInPopup);
      // la excepción lanzada pasara por los operadores RxJS catchError, finalize
      // y por las funciones error y complete de un subscribe.
      // No pasa por un complete de un subscribe !!!
      //
      // Para ser capturada debera hacerse:
      // catch (error: any) {
      //   if (error instanceof ValidationServerException)  {
      //     let validationResult = error.validationResult;
      //     ...
      //   } else {
      //     ...BaseFormComponent
      //   }
      // }
      return throwError(new ValidationServerException(validation));
    } else {
      this.errorHandler.showServerError(error);
      return throwError(error);
    }
  }

  protected validate(showWait = true, model?: T): Observable<ValidationResultDTO> {
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));
    let validationCtx = modifiedDto.identifier ? 'Update' : 'Insert';
    return this.validateContext(showWait, model, validationCtx);
  }

  protected validateContext(showWait = true, model?: T, validationCtx?: string): Observable<ValidationResultDTO> {
    if (showWait) {
      this.waitService.startWaiting();
    }
    let modifiedDto = model
      ? model
      : this.modelToDTO(lodash.cloneDeep(this.model));
    this.setTaskReference(modifiedDto);
    //let validationCtx = modifiedDto.identifier ? 'Update' : 'Insert';
    return this.plycaService.validate<T>(
      this.getServiceURL('validate'),
      validationCtx,
      modifiedDto
    ).pipe(
      tap((validation: ValidationResultDTO) => {
        if (this.formPlycaRef) {
          this.formPlycaRef.deleteFormErrors();
        }
        if (validation.result !== ResultTypeDTO.OK) {
          // controlar cualquier tipo de error de validacion
          this.manageActionError(validation, false); // cancelWait = false por se hace después en el finalize
        }
      }),
      catchError((error: any) => this.manageHttpError(error, false)), // cancelWait = false por se hace después en el finalize
      catchError((error: any) => {
        if (error instanceof ValidationServerException) {
          // con esto hacemos que la excepcion se capture y el resultado llegue como si fuera una peticion correcta
          // el resultado llegará al siguiente operador de un pipe y al next del subscribe
          return of(error.validationResult);
        } else {
          return throwError(error);
        }
      }),
      finalize(() => {
        if (showWait) {
          this.waitService.cancelWaiting();
        }
      })
    );
  }

  private setTaskReference(modifiedDto: T) {
    if (this.task) {
      // en el caso de tareas con recursos se envía la referencia a la tarea
      // para que el servidor obtenga las variables de contexto de la misma
      modifiedDto.task = {} as TaskDTO;
      modifiedDto.task.identifier = this.task.identifier;
    }
  }

  /**
   * Metodos a implementar
   */

  abstract getFormSettingURL(): string;
  abstract getServiceURL(operation?: string): string;
  abstract getObjectIdentifier();
  abstract dtoToModel(dto: T): Observable<U>;
  abstract modelToDTO(model: U): T;
  abstract getHeaderInfo(): BaseFormHeaderInfo;
  abstract initHeaderActions();

  getObjectParams(): { [param: string]: string | string[] } {
    return undefined;
  }

  configureFormConfig(formConfig: FormConfig): Observable<FormConfig> {
    if (formConfig.isModalWindow) {
      this.windowHash = generateRandomIdentifier();
    }
    formConfig.showValidations = true;
    return of(formConfig);
  }

  /**
   * Metodos de utilidad
   */

  protected addBackAction() {
    this.subscribe(this.headerActionsService
      .addWindowAction(
        'General.back',
        'fas fa-chevron-left',
        'btn-outline-secondary',
        true,
        this.windowHash
      ), () => {
        this.salir();
      });
  }



  /**
   * Impedir salir si hay modal abierta
   */
  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (!this.modalOpened) {
      return super.canDeactivate();
    }
    return false;
  }

  createRejectionPanel() {
    try {
      let _document = this.getDocument();
      if (_document) {
        // this.formConfig.showValidations = true;
        this.rejectionPanelContainer.clear();

        const childComponent = this.componentFactoryResolver.resolveComponentFactory(
          formPlycaComponent
        );
        // at this point we want the "child" component to be rendered into the app.component:
        let rejectionPanel = this.rejectionPanelContainer.createComponent(childComponent).instance;
        rejectionPanel['document'] = _document;
      }
    } catch { console.log('ERROR CALCULANDO EL MENSAJE DE DEVOLUCIÓN') }
  }

  // SOBREESCRIBIR ESTE METODO SI LA FORMA DE RECUPERAR EL DOCUMENTO NO ES LA ADECUADA
  getDocument(): DocumentDTO {
    try {
      return this.model['document'];
    } catch { }
  }
}
